import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { EMPTY_ARRAY } from "@/lib/utils/stable-empty";
import { useQuery as useApolloQuery, useMutation as useApolloMutation } from "@apollo/client/react";
import {
  GetSkillsCatalogAllDocument,
  GetAppUserSkillsDocument,
  InsertAppUserSkillDocument,
  UpdateAppUserSkillDocument,
  DeleteAppUserSkillDocument,
  type GetSkillsCatalogAllQuery,
  type GetAppUserSkillsQuery,
  type InsertAppUserSkillMutation,
  type UpdateAppUserSkillMutation,
  type DeleteAppUserSkillMutation,
} from "@/src/gql/graphql";
import type { SkillCatalog, AppUserSkill } from "@/lib/types/skills";

const SKILLS_QUERY_KEY = ["profile-skills"];

function mapCatalogFromGraphQL(
  edges: NonNullable<GetSkillsCatalogAllQuery["skills_catalogCollection"]>["edges"]
): SkillCatalog[] {
  return edges.map((e) => ({
    id: e.node.id,
    name: e.node.name,
    category: e.node.category,
    sort_order: e.node.sort_order,
    created_at: e.node.created_at ?? undefined,
  }));
}

function mapUserSkillsFromGraphQL(
  edges: NonNullable<GetAppUserSkillsQuery["app_user_skillsCollection"]>["edges"]
): AppUserSkill[] {
  return edges.map((e) => ({
    user_id: e.node.user_id,
    skill_id: e.node.skill_id,
    grade: e.node.grade,
    created_at: e.node.created_at ?? undefined,
    updated_at: e.node.updated_at ?? undefined,
    skill: e.node.skills_catalog
      ? {
          id: e.node.skills_catalog.id,
          name: e.node.skills_catalog.name,
          category: e.node.skills_catalog.category,
          sort_order: e.node.skills_catalog.sort_order,
          created_at: undefined,
        }
      : undefined,
  }));
}

export function useSkillsCatalog() {
  const { data, loading, error, refetch } = useApolloQuery(GetSkillsCatalogAllDocument, {
    variables: { orderBy: [{ sort_order: "AscNullsLast" }, { name: "AscNullsLast" }] },
  });

  const catalog = data?.skills_catalogCollection?.edges
    ? mapCatalogFromGraphQL(data.skills_catalogCollection.edges)
    : undefined;

  return {
    data: catalog,
    isLoading: loading,
    error: error ?? null,
    refetch,
  };
}

export function useProfileSkills(userId: string | undefined) {
  const { data, loading, error, refetch } = useApolloQuery(GetAppUserSkillsDocument, {
    variables: {
      filter: userId ? { user_id: { eq: userId } } : undefined,
      orderBy: [{ grade: "DescNullsLast" }],
    },
    skip: !userId,
  });

  const userSkillEdges = data?.app_user_skillsCollection?.edges;
  // Mapped once per Apollo result, not once per render: `data` is a dependency
  // for consumers, and the mapper always allocates a new array.
  const userSkills = useMemo(
    () => (userSkillEdges ? mapUserSkillsFromGraphQL(userSkillEdges) : EMPTY_ARRAY),
    [userSkillEdges]
  );

  return {
    data: userSkills,
    isLoading: loading,
    error: error ?? null,
    refetch,
  };
}

export function useAddProfileSkill(userId: string | undefined) {
  const queryClient = useQueryClient();
  const [insertSkill] = useApolloMutation<InsertAppUserSkillMutation>(InsertAppUserSkillDocument);

  return useMutation({
    mutationFn: async ({
      skillId,
      grade,
    }: {
      skillId: string;
      grade: number;
    }) => {
      if (!userId) throw new Error("User ID required");
      const { data } = await insertSkill({
        variables: {
          input: {
            user_id: userId,
            skill_id: skillId,
            grade: Math.min(5, Math.max(1, grade)),
            updated_at: new Date().toISOString(),
          },
        },
        refetchQueries: [
          {
            query: GetAppUserSkillsDocument,
            variables: {
              filter: { user_id: { eq: userId } },
              orderBy: [{ grade: "DescNullsLast" }],
            },
          },
        ],
      });
      const record = data?.insertIntoapp_user_skillsCollection?.records?.[0];
      if (!record) throw new Error("Failed to add skill");
      return record;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...SKILLS_QUERY_KEY, userId] });
    },
  });
}

export function useUpdateProfileSkill(userId: string | undefined) {
  const queryClient = useQueryClient();
  const [updateSkill] = useApolloMutation<UpdateAppUserSkillMutation>(UpdateAppUserSkillDocument);

  return useMutation({
    mutationFn: async ({
      skillId,
      grade,
    }: {
      skillId: string;
      grade: number;
    }) => {
      if (!userId) throw new Error("User ID required");
      const { data } = await updateSkill({
        variables: {
          filter: {
            and: [{ user_id: { eq: userId } }, { skill_id: { eq: skillId } }],
          },
          set: {
            grade: Math.min(5, Math.max(1, grade)),
            updated_at: new Date().toISOString(),
          },
        },
        refetchQueries: [
          {
            query: GetAppUserSkillsDocument,
            variables: {
              filter: { user_id: { eq: userId } },
              orderBy: [{ grade: "DescNullsLast" }],
            },
          },
        ],
      });
      const record = data?.updateapp_user_skillsCollection?.records?.[0];
      if (!record) throw new Error("Failed to update skill");
      return record;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...SKILLS_QUERY_KEY, userId] });
    },
  });
}

export function useRemoveProfileSkill(userId: string | undefined) {
  const queryClient = useQueryClient();
  const [deleteSkill] = useApolloMutation<DeleteAppUserSkillMutation>(DeleteAppUserSkillDocument);

  return useMutation({
    mutationFn: async (skillId: string) => {
      if (!userId) throw new Error("User ID required");
      const { data } = await deleteSkill({
        variables: {
          filter: {
            and: [{ user_id: { eq: userId } }, { skill_id: { eq: skillId } }],
          },
        },
        refetchQueries: [
          {
            query: GetAppUserSkillsDocument,
            variables: {
              filter: { user_id: { eq: userId } },
              orderBy: [{ grade: "DescNullsLast" }],
            },
          },
        ],
      });
      const records = data?.deleteFromapp_user_skillsCollection?.records;
      if (!records || records.length === 0) throw new Error("Failed to remove skill");
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...SKILLS_QUERY_KEY, userId] });
    },
  });
}
